// Stripe configuration
export const STRIPE_CONFIG = {
  // Stripe publishable key - should be set in environment variables
  publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51ROSBhCXT4tazNH1Wwdtc8cjzJiuZWCRfclgham6T7e1XF1sgJdGN9zUMMp1P2C4ccLmBA1XJZbTVSVQytX7H36n0078TXaoaw',
  
  // API endpoints
  endpoints: {
    createCheckoutSession: '/api/create-checkout-session',
    processInvitationPayment: '/api/process-invitation-payment',
    sessionStatus: '/api/session-status',
  },
  
  // Pricing configuration
  pricing: {
    perInviteCost: 15, // $15 per invitation
    currency: 'usd',
  },
  
  // Checkout configuration
  checkout: {
    successUrl: `${window.location.origin}/checkout?session_id={CHECKOUT_SESSION_ID}&mode=complete`,
    cancelUrl: `${window.location.origin}/invitation-management`,
  },
  
  // Payment method types
  paymentMethodTypes: ['card'],
  
  // Appearance settings
  appearance: {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#2563eb',
      colorBackground: '#ffffff',
      colorText: '#1f2937',
      colorDanger: '#dc2626',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  },
};

// Environment-specific configuration
export const getStripeConfig = () => {
  const isProduction = import.meta.env.PROD;

  // Get the key from environment variables
  const envKey = isProduction
    ? import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_LIVE
    : import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  // Validate the key format
  if (envKey) {
    if (envKey.startsWith('sk_')) {
      // A secret key must never reach the frontend. Do NOT log the key value.
      console.error('❌ ERROR: A Stripe SECRET KEY (sk_...) was provided to the frontend.');
      console.error('✅ Use a PUBLISHABLE KEY (pk_test_... or pk_live_...) instead — set VITE_STRIPE_PUBLISHABLE_KEY.');
      console.warn('⚠️  Falling back to the configured publishable key. Update your env configuration.');
      // Fall back to default instead of crashing
      const fallbackKey = STRIPE_CONFIG.publishableKey;
      return {
        ...STRIPE_CONFIG,
        publishableKey: fallbackKey, // Use default fallback
      };
    }
    if (!envKey.startsWith('pk_')) {
      console.error('❌ ERROR: Invalid Stripe key format.');
      console.error('✅ Stripe publishable keys must start with "pk_test_" or "pk_live_"');
      console.warn('⚠️  Falling back to default test key. Please update your .env.development file!');
      const fallbackKey = STRIPE_CONFIG.publishableKey;
      return {
        ...STRIPE_CONFIG,
        publishableKey: fallbackKey, // Use default fallback
      };
    }
    
    // Valid publishable key found
  } else {
    console.warn('⚠️  No publishable key found in environment variables');
  }
  
  const finalKey = envKey || STRIPE_CONFIG.publishableKey;
  return {
    ...STRIPE_CONFIG,
    // Override settings based on environment
    publishableKey: finalKey,
  };
};

// Safety helper: Ensures we NEVER pass a secret key to loadStripe
export const getSafePublishableKey = (): string => {
  const config = getStripeConfig();
  let key = config.publishableKey;
  
  // Final safety check - NEVER allow secret keys
  if (key.startsWith('sk_')) {
    console.error('🚨 CRITICAL ERROR: Secret key detected in getSafePublishableKey!');
    console.error('🚨 This should never happen - using fallback key');
    key = 'pk_test_51ROSBhCXT4tazNH1Wwdtc8cjzJiuZWCRfclgham6T7e1XF1sgJdGN9zUMMp1P2C4ccLmBA1XJZbTVSVQytX7H36n0078TXaoaw';
  }
  
  if (!key.startsWith('pk_')) {
    console.error('🚨 CRITICAL ERROR: Invalid key format in getSafePublishableKey!');
    console.error('🚨 Using fallback key');
    key = 'pk_test_51ROSBhCXT4tazNH1Wwdtc8cjzJiuZWCRfclgham6T7e1XF1sgJdGN9zUMMp1P2C4ccLmBA1XJZbTVSVQytX7H36n0078TXaoaw';
  }
  
  return key;
};

// Validation helpers
export const validateStripeConfig = () => {
  try {
    const config = getStripeConfig();
    
    if (!config.publishableKey) {
      console.error('❌ Stripe publishable key is not configured');
      console.error('✅ Set VITE_STRIPE_PUBLISHABLE_KEY or VITE_STRIPE_PUBLISHABLE_KEY_TEST in your .env file');
      return false;
    }
    
    if (!config.publishableKey.startsWith('pk_')) {
      console.error('❌ Invalid Stripe publishable key format');
      console.error('✅ Stripe publishable keys must start with "pk_test_" or "pk_live_"');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('❌ Stripe configuration validation failed:', error);
    return false;
  }
};

// Type definitions for Stripe integration
export interface StripeCheckoutData {
  amount: number;
  currency: string;
  inviteCount: number;
  clinicianNames: string[];
  practiceId: string;
  clinicianIds: string[];
  metadata: Record<string, string>;
}

export interface StripeCheckoutResponse {
  sessionId: string;
  clientSecret?: string;
  url?: string;
}

export interface StripePaymentResult {
  success: boolean;
  paymentIntentId?: string;
  error?: string;
}

